import React, { useState, useCallback } from 'react';
import { Upload, FileText, X, Eye, Download, CheckCircle, AlertCircle } from 'lucide-react';

const DocumentUploader = ({ documentId, requiredDocuments, onUpload, existingFiles = [], documentType = 'general' }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [errors, setErrors] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState(existingFiles);

  const acceptedTypes = {
    'pdf': 'application/pdf',
    'doc': 'application/msword',
    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'png': 'image/png',
    'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'xls': 'application/vnd.ms-excel',
    'txt': 'text/plain'
  };

  const maxFileSize = 10 * 1024 * 1024; // 10MB

  // Health permit specific document categories
  const healthPermitCategories = {
    'application': 'Application Documents',
    'plans': 'Site Plans & Specifications',
    'certifications': 'Certifications & Licenses',
    'permits': 'Building Permits',
    'menus': 'Menu & Consumer Advisories',
    'other': 'Other Required Documents'
  };

  const validateFile = (file) => {
    const errors = [];
    
    if (file.size > maxFileSize) {
      errors.push(`File "${file.name}" is too large. Maximum size is 10MB.`);
    }
    
    const extension = file.name.split('.').pop().toLowerCase();
    if (!acceptedTypes[extension]) {
      errors.push(`File type "${extension}" is not supported. Accepted types: PDF, DOC, DOCX, JPG, PNG, XLSX`);
    }
    
    return errors;
  };

  const categorizeHealthPermitDocument = (fileName) => {
    const name = fileName.toLowerCase();
    
    if (name.includes('application') || name.includes('permit') || name.includes('form')) {
      return 'application';
    } else if (name.includes('plan') || name.includes('specification') || name.includes('layout') || name.includes('blueprint')) {
      return 'plans';
    } else if (name.includes('certificate') || name.includes('servsafe') || name.includes('allergen') || name.includes('license')) {
      return 'certifications';
    } else if (name.includes('building') || name.includes('construction') || name.includes('inspection')) {
      return 'permits';
    } else if (name.includes('menu') || name.includes('advisory') || name.includes('consumer')) {
      return 'menus';
    } else {
      return 'other';
    }
  };

  const handleFiles = useCallback((files) => {
    const fileArray = Array.from(files);
    const allErrors = [];
    const validFiles = [];

    fileArray.forEach(file => {
      const fileErrors = validateFile(file);
      if (fileErrors.length > 0) {
        allErrors.push(...fileErrors);
      } else {
        validFiles.push(file);
      }
    });

    setErrors(allErrors);

    validFiles.forEach(file => {
      // Simulate upload progress
      const fileId = Date.now() + Math.random();
      setUploadProgress(prev => ({ ...prev, [fileId]: 0 }));

      const interval = setInterval(() => {
        setUploadProgress(prev => {
          const progress = prev[fileId] + 10;
          if (progress >= 100) {
            clearInterval(interval);
            const fileData = {
              id: fileId,
              name: file.name,
              type: file.type,
              size: file.size,
              uploadedAt: new Date().toISOString(),
              url: URL.createObjectURL(file), // In real app, this would be server URL
              category: documentType === 'health-permit' ? categorizeHealthPermitDocument(file.name) : 'general'
            };
            
            setUploadedFiles(prev => [...prev, fileData]);
            onUpload(documentId, fileData);
            return { ...prev, [fileId]: 100 };
          }
          return { ...prev, [fileId]: progress };
        });
      }, 200);
    });
  }, [documentId, onUpload]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleFileInput = useCallback((e) => {
    handleFiles(e.target.files);
  }, [handleFiles]);

  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf':
        return <FileText className="w-8 h-8 text-red-500" />;
      case 'doc':
      case 'docx':
        return <FileText className="w-8 h-8 text-blue-500" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
        return <FileText className="w-8 h-8 text-green-500" />;
      case 'xlsx':
      case 'xls':
        return <FileText className="w-8 h-8 text-emerald-500" />;
      default:
        return <FileText className="w-8 h-8 text-gray-500" />;
    }
  };

  const groupFilesByCategory = (files) => {
    if (documentType !== 'health-permit') {
      return { 'general': files };
    }
    
    return files.reduce((groups, file) => {
      const category = file.category || 'other';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(file);
      return groups;
    }, {});
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          isDragOver
            ? 'border-purple-400 bg-purple-50'
            : 'border-gray-300 hover:border-purple-400'
        }`}
      >
        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Upload Documents
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Drag and drop files here, or click to browse
        </p>
        
        <input
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx"
          onChange={handleFileInput}
          className="hidden"
          id={`file-upload-${documentId}`}
        />
        <label
          htmlFor={`file-upload-${documentId}`}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 cursor-pointer"
        >
          Select Files
        </label>
        
        <p className="text-xs text-gray-500 mt-2">
          Supported formats: PDF, DOC, DOCX, JPG, PNG, XLSX (Max 10MB each)
        </p>
      </div>

      {/* Required Documents Checklist */}
      {requiredDocuments && requiredDocuments.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-3">Required Documents</h4>
          <div className="space-y-2">
            {requiredDocuments.map((doc, index) => {
              const isUploaded = existingFiles.some(file => 
                file.name.toLowerCase().includes(doc.toLowerCase().split(' ')[0])
              );
              
              return (
                <div key={index} className="flex items-center justify-between p-2 bg-white rounded">
                  <span className="text-sm text-gray-700">{doc}</span>
                  {isUploaded ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-yellow-500" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Upload Progress */}
      {Object.keys(uploadProgress).length > 0 && (
        <div className="space-y-2">
          {Object.entries(uploadProgress).map(([fileId, progress]) => (
            <div key={fileId} className="bg-white border rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Uploading...</span>
                <span className="text-sm text-gray-500">{progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Error Messages */}
      {errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <AlertCircle className="w-4 h-4 text-red-500" />
            <h4 className="font-medium text-red-800">Upload Errors</h4>
          </div>
          <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
          <button
            onClick={() => setErrors([])}
            className="mt-2 text-sm text-red-600 hover:text-red-800"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Existing Files */}
      {existingFiles.length > 0 && (
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">Uploaded Files</h4>
          {Object.entries(groupFilesByCategory(existingFiles)).map(([category, files]) => (
            <div key={category} className="space-y-2">
              {documentType === 'health-permit' && (
                <h5 className="text-sm font-medium text-gray-700 border-b border-gray-200 pb-1">
                  {healthPermitCategories[category] || 'Other Documents'}
                </h5>
              )}
              <div className="grid grid-cols-1 gap-3">
                {files.map((file) => (
                  <div key={file.id} className="flex items-center space-x-3 p-3 bg-white border border-gray-200 rounded-lg hover:shadow-sm transition-shadow">
                    {getFileIcon(file.name)}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                      <p className="text-xs text-gray-500">
                        {formatFileSize(file.size)} • Uploaded {new Date(file.uploadedAt).toLocaleDateString()}
                        {file.category && documentType === 'health-permit' && (
                          <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                            {healthPermitCategories[file.category]}
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => window.open(file.url, '_blank')}
                        className="p-1 text-gray-400 hover:text-blue-600"
                        title="View file"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          const link = document.createElement('a');
                          link.href = file.url;
                          link.download = file.name;
                          link.click();
                        }}
                        className="p-1 text-gray-400 hover:text-green-600"
                        title="Download file"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          // In real app, would call API to delete file
                          console.log('Delete file:', file.id);
                        }}
                        className="p-1 text-gray-400 hover:text-red-600"
                        title="Delete file"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DocumentUploader; 